import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { User } from '@stockcare-storage/user/user';
import { UserStorageService } from '@stockcare-storage/user/user-storage.service';
import { AuthenticationService } from '@stockcare-services/authentication/authentication.service';
import { ActivityService } from '@stockcare-services/activity-service/activity.service';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { Roles } from '@stockcare-helpers/roles';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';

@Component({
  standalone: false,
  selector: 'app-desktop-pin-login',
  templateUrl: './desktop-login-pin.component.html',
  styleUrls: ['./desktop-login-pin.component.scss'],
})
export class DesktopLoginPinComponent implements OnInit {
  loginPinForm: UntypedFormGroup;
  users: User[] = [];
  submitted: boolean = false;
  alert: string;
  dismissible: boolean = true;
  loginError: boolean = false;
  isTrainingRequested:  boolean = false;
  isTrainingRequestsEnabled: boolean = false;

  constructor(
    private router: Router,
    private fb: UntypedFormBuilder,
    private userStorageService: UserStorageService,
    private deviceService: DeviceService,
    private authService: AuthenticationService,
    private activityService: ActivityService,
    private settingsService: SettingsService
  ) {}

  async ngOnInit() {
    this.loginPinForm = this.fb.group({
      username: [null, [Validators.required]],
      pin: [null, [Validators.required]],
    });
    this.users = await this.getSessionUsers();
    this.isTrainingRequestsEnabled = this.settingsService.getBooleanSetting(Settings.ENABLE_REQUEST_TRAINING);
  }

  get controls() {
    return this.loginPinForm.controls;
  }

  compareFn(user1: User, user2: User) {
    return user1 && user2 ? user1.Id === user2.Id : user1 === user2;
  }

  async getSessionUsers(): Promise<User[]> {
    return await this.userStorageService.getAllUsers();
  }

  navigateToLogin() {
    this.router.navigate(['/app/authentication/login']);
  }

  onClosed() {
    this.loginError = false;
  }

  async loginAndNavigateToRequestTraining() {
    this.isTrainingRequested = true;
    this.login();
  }

  async login() {
    const username = this.loginPinForm.get('username').value;
    const pin = this.loginPinForm.get('pin').value;

    this.authService.loginWithPin(username, pin).subscribe((isValidPin) => {
      if (isValidPin) {
        this.onLoggedIn();
      } else {
        this.loginError = true;
        this.alert = 'Invalid Pin, Please Try Again';
      }
    });
  }

  private onLoggedIn() {
    if (this.authService.isActionCenterLogin) {
      this.navigateToActionCenter();
      return;
    }
    const isLinked = this.deviceService.isDeviceLinked();
    const isAdministrator = this.authService.isAdministrator();

    if (!isLinked) {
      this.linkDevice(isAdministrator);
      return;
    }

    if (this.deviceService.isDeviceRemoved()) {
      this.activateDevice();
      return;
    }

    this.activityService.start();

    if (this.isTrainingRequested) {
      this.navigateToRequestTraining();
      return;
    }

    if (this.deviceService.isWhiteboardDevice()) {
      this.navigateToWhiteBoard();
      return;
    }

    if (this.deviceService.isTheaterDevice()) {
      this.navigateToTheater();
      return;
    }

    if (this.deviceService.isEmergencyCupboardDevice()) {
      this.navigateToEmergencyCupboard();
      return;
    }

    var canViewPatients = this.authService.isInRole([
      Roles.systemAdministrator,
      Roles.superAdministrator,
      Roles.stockIssuer,
      Roles.creditRequester,
    ]);

    if (canViewPatients) {
      this.navigateToHome();
      return;
    }

    var canApproveCredits = this.authService.isInRole([Roles.systemAdministrator, Roles.superAdministrator, Roles.creditApprover]);

    if (canApproveCredits) {
      this.router.navigate(['app/approvals']);
      return;
    }

    this.router.navigate(['app/baskets']);
    return;
  }

  private linkDevice(isAdministrator: boolean) {
    this.router.navigate([isAdministrator ? 'app/authentication/linkdevice' : 'app/authentication/deviceneedstobelinked']);
  }

  private navigateToActionCenter() {
    this.router.navigate(['/app/action-center']);
  }

  private activateDevice() {
    this.router.navigate(['app/authentication/devicedeactivated']);
  }

  private navigateToHome() {
    this.router.navigate(['/app/patients']);
  }

  private navigateToWhiteBoard() {
    this.router.navigate(['/app/whiteboard']);
  }

  private navigateToRequestTraining() {
    this.router.navigate(['/app/request-training/new-request']);
  }

  private navigateToTheater() {
    this.router.navigate(['/app/theatre']);
  }

  private navigateToEmergencyCupboard() {
    this.router.navigate(['app/emergency-cupboard']);
  }
}